import type { UIMessagePart } from '../../meta/build-assistant-message-meta';
import type { NextPromptInput } from '../../types/transport';
import { extractText } from '../MessageList/message-text';

/** Action cards that wait for the user's click. A turn that ends in one
 *  already offers its own answers, so it gets no guessed next prompt. */
const AWAITING_ACTION_TYPES = new Set(['prompt', 'form', 'email']);

/** The endpoint's body limits. The server only reads the reply's tail, so a
 *  long reply keeps its end rather than failing the request. */
const MAX_USER_MESSAGE_CHARS = 8000;
const MAX_ASSISTANT_TEXT_CHARS = 20000;

interface MessageLike {
  readonly role?: string;
  readonly parts?: UIMessagePart[];
}

function hasAwaitingAction(parts: UIMessagePart[]): boolean {
  return parts.some((part) => {
    // An A2UI surface (a change card, an email draft) is answered with a click,
    // so a typed guess would only offer to do what the card already does.
    if (part.type === 'data-a2ui') return true;
    if (part.type !== 'data-actions') return false;
    const actions = part.data?.['actions'];
    return (
      Array.isArray(actions) &&
      actions.some((a) => AWAITING_ACTION_TYPES.has(a?.type))
    );
  });
}

/**
 * What to send for a next-prompt guess after a finished turn, or `null`
 * when the turn gets none: no conversation id, a card or A2UI surface
 * awaiting the user's click, or no reply text. `messages` is the full list at finish time; the
 * user's message is the last user turn before the reply.
 */
export function nextPromptInputFor(
  conversationId: string | null,
  message: MessageLike | undefined,
  messages: ReadonlyArray<unknown>
): NextPromptInput | null {
  if (!conversationId || !message?.parts) return null;
  if (hasAwaitingAction(message.parts)) return null;
  const assistantText = extractText(message.parts).trim();
  if (!assistantText) return null;
  const lastUser = (messages as ReadonlyArray<MessageLike>).findLast(
    (m) => m?.role === 'user'
  );
  return {
    conversationId,
    userMessage: extractText(lastUser?.parts)
      .trim()
      .slice(0, MAX_USER_MESSAGE_CHARS),
    assistantText: assistantText.slice(-MAX_ASSISTANT_TEXT_CHARS),
  };
}
